import type { Document as RichTextDocument } from "@contentful/rich-text-types";

type RichTextNode = {
  nodeType?: string;
  value?: string;
  content?: Array<RichTextNode>;
  data?: Record<string, unknown>;
};

const TEXTUAL_NODES = new Set([
  "document",
  "text",
  "paragraph",
  "heading-1",
  "heading-2",
  "heading-3",
  "heading-4",
  "heading-5",
  "heading-6",
  "hyperlink",
  "blockquote",
]);

/**
 * Flatten a Contentful rich-text document to the plain text a single-line
 * split animation needs.
 *
 * QC-006 / R-117: `AnimatedSplitLines` with `animationType="clipPathReveal"`
 * warns (and animates unreliably across screen sizes) when given JSX children.
 * Call sites that only need body copy now pass the flattened text instead,
 * so the per-line reveal splits on real rendered lines.
 *
 * Non-textual blocks (embedded assets/entries, tables, etc.) contribute a
 * space so adjacent words stay separated. Inline footnote/embedded-entry
 * markers are intentionally dropped — they are decorative superscripts in
 * this context.
 */
export const richTextToPlainText = (
  json: RichTextDocument | unknown | null | undefined
): string => {
  if (!json || typeof json !== "object") return "";

  const buffer: Array<string> = [];

  const visit = (node: RichTextNode) => {
    if (!node || typeof node !== "object") return;

    if (node.nodeType === "text" && typeof node.value === "string") {
      buffer.push(node.value);
      return;
    }

    if (node.nodeType && !TEXTUAL_NODES.has(node.nodeType)) {
      // non-textual block/inline node — keep word separation only
      buffer.push(" ");
      return;
    }

    node.content?.forEach(visit);

    if (node.nodeType === "paragraph" || node.nodeType?.startsWith("heading")) {
      buffer.push(" ");
    }
  };

  visit(json as RichTextNode);

  return buffer
    .join("")
    .replace(/\s+/g, " ")
    .trim();
};
